import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { AuthService } from '@auth';
import { LockService } from './core/services/lock.service';
import { ShortcutInput, AllowIn } from 'ng-keyboard-shortcuts';
import { Router, NavigationEnd } from '@angular/router';
import { VersionCheckService } from './core/services/version-check.service';
import { environment } from 'src/environments/environment';
import { SwitchLanguageService } from './core/services/switch-language.service';
import { OnlineOfflineService } from './core/services/online-offine.service';
import * as Sentry from "@sentry/angular";
import { IntercomEdaraConfig } from './shared/models/intercom-config';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Intercom } from '@supy-io/ngx-intercom';
import { PotentialFraudDetectorService } from './core/services/potential-fraud-detector.service';
import { AppService } from './core/services/app.service';
import { DexieDbProvider, HelpersService } from '@shared';
import { PagesMenuService } from '@shared/services/pages-menu.service';
import { PageMenuDateService } from '@shared/services/page-menu-date.service';
import { SignalrService } from '@core/services/signalr.service';
import { BroadcastChannel } from 'broadcast-channel';
import { EdaraSettingsService, SyncService, SystemSettingsService } from '@core';
import { MatDialog } from '@angular/material/dialog';
import { MultiTabsBlockingDialogComponent } from '@core/components/multi-tabs-blocking-dialog/multi-tabs-blocking-dialog.component';
import { PosthogService } from '@core/services/posthog.service';
import { EmbedService } from '@core/services/embed.service';
import { PortalSyncService } from '@core/services/portal-sync.service';
import { KnownDeviceIdentityService } from '@core/services/known-device-identity.service';
import { KnownDeviceRevocationService } from '@core/services/known-device-revocation.service';
import { CrossTabSignOutService } from '@core/services/cross-tab-sign-out.service';

// Declare gtag as a function to set and sent the events
declare var gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {

  title = 'Edara';
  intercomMessages?: number;
  shortcuts: ShortcutInput[] = [];
  isAuthenticated = false;
  showLockScreen = false;
  fullLayoutEnabled = false;
  /**
   * Running as a product inside the Edara Portal, which renders the top bar and side navigation
   * for us. Read from the template to drop this app's own copies of that chrome.
   */
  isEmbedded = false;

  readonly tabLockChannel = new BroadcastChannel('tab-lock');
  tabId: number = Date.now(); // Use timestamp as initial ID
  isOldestTab: boolean = false;

  constructor(
    public appService: AppService,
    public helpersService: HelpersService,
    public onlineOfflineService: OnlineOfflineService,
    private authService: AuthService,
    private lockService: LockService,
    private versionCheckService: VersionCheckService,
    private switchLanguageService: SwitchLanguageService,
    private router: Router,
    private translateService: TranslateService,
    private intercom: Intercom,
    private potentialFraudDetectorService: PotentialFraudDetectorService,
    private menuService: PagesMenuService,
    private pageMenuDateService: PageMenuDateService,
    private localDbProvider: DexieDbProvider,
    private signalRService: SignalrService,
    private systemSettingsService: SystemSettingsService,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private syncService: SyncService, // Needed Due to it's constructor
    private edaraSettingsService: EdaraSettingsService, // Needed Due to it's constructor
    private posthogService: PosthogService,
    private embedService: EmbedService,
    private portalSyncService: PortalSyncService,
    private knownDeviceIdentityService: KnownDeviceIdentityService,
    private knownDeviceRevocationService: KnownDeviceRevocationService,
    private crossTabSignOutService: CrossTabSignOutService
  ) {
    this.isEmbedded = this.embedService.isEmbedded;
    this.switchLanguageService.useCurrentLanguage();
    this.appService.init();
  }

  ngOnInit() {
    this.isAuthenticated = this.authService.isAuthenticated();

    // Mirror the portal's organization/language/theme into this frame, and report back whether the
    // frame has a usable session. No-op when not embedded.
    this.portalSyncService.start();

    if (this.onlineOfflineService.isOnline) {
      this.versionCheckService.initVersionCheck(window.location.origin + environment.versionCheckURL);

      if (localStorage.getItem('isLoggedIn') === 'true') {
        this.authService.renewToken();
        this.authService.renewFirebaseToken();
        this.authService.renewShopifyFirebaseToken();
      }
    }

    if (this.authService.isAuthenticated()) {
      // Identify user for Google Analytics (Set the user ID using signed-in user_id)
      gtag('set', 'userId', this.authService.currentUserEmail);

      this.setUserContextToSentry();

      this.initIntercom();

      this.knownDeviceIdentityService.resolveDevice();
      // Sign out if an administrator removes this device from Known Devices (Bug 28014).
      this.knownDeviceRevocationService.watch();
      // Reload this tab into sign-in once another tab of this browser signs out.
      this.crossTabSignOutService.watch();

      if (!this.isEmbedded) {
        this.lockService.passcodeLoaded.subscribe({
          next: () => {
            if (this.lockService.isExpired()) {
              this.lockService.show();
            } else {
              this.lockService.startWatching();
            }
          }
        });
      }

      this.appService.setBodyPadding();

      this.menuService.loadStarredPagesList();

      this.pageMenuDateService.loadOpenedPagesList();

      // Connect to SignalR
      this.signalRService.startConnection(this.authService.currentTenant);

      this.handleMultiTabs();
    }

    this.authService.userLoggedIn$.subscribe({
      next: () => {
        this.isAuthenticated = true;

        // Identify user for Google Analytics (Set the user ID using signed-in user_id)
        gtag('set', 'userId', this.authService.currentUserEmail);

        this.setUserContextToSentry();

        // load intercom after login
        this.initIntercom();

        this.knownDeviceIdentityService.resolveDevice();
        this.knownDeviceRevocationService.watch();
        this.crossTabSignOutService.watch();

        this.appService.setBodyPadding();

        this.menuService.loadStarredPagesList();

        this.pageMenuDateService.loadOpenedPagesList();

        // Connect to SignalR
        this.signalRService.startConnection(this.authService.currentTenant);

        this.handleMultiTabs();
      }
    });

    // Delete local data from indexedDb on switch tenanat
    this.authService.tenantSwitched$.subscribe({
      next: () => {
        this.localDbProvider.deleteDatabase();
      }
    });

    this.appService.onFullLayoutEnabled().subscribe({
      next: (enabled: boolean) => this.fullLayoutEnabled = enabled
    });

    this.lockService.change.subscribe({
      next: (isLocked: boolean) => {
        this.showLockScreen = isLocked;
      }
    });

    this.router.events.subscribe({
      next: (event) => {
        if (this.onlineOfflineService.isOnline) {
          if (event instanceof NavigationEnd && !event.url.startsWith('/callback')) {
            // Send page views to Google Analytics
            gtag('set', 'page', event.urlAfterRedirects);
            gtag('send', 'pageview');

            // Refresh access token on page navigation
            this.authService.renewToken();
            this.authService.renewFirebaseToken();
            this.authService.renewShopifyFirebaseToken();
          }
        }
      }
    });

    this.onlineOfflineService.connectionChanged.subscribe({
      next: () => {
        // Show fresh chat if online, otherwise hide it
        const edaraChat = document.getElementById('edarachat');
        if (edaraChat) {
          edaraChat.style.visibility = this.onlineOfflineService.isOnline ? 'visible' : 'hidden';
        }
      }
    });

    //Initialize Potential Fraud Detector Service
    this.potentialFraudDetectorService.start();
    // Initialize PostHog
    this.posthogService.initPostHog();
  }

  ngAfterViewInit() {
    this.initShortcuts();
  }

  ngAfterContentChecked() {
    this.changeDetectorRef.detectChanges();
  }

  toggleSideNav() {
    if (this.appService.state.navState === 'expanded')
      this.appService.handleNavState('collapsed');
    else if (this.appService.state.navState === 'collapsed')
      this.appService.handleNavState('closed');
    else if (this.appService.state.navState === 'closed')
      this.appService.handleNavState('expanded');
  }

  private initIntercom() {
    // The portal provides support chat around the frame, so booting a second Intercom messenger
    // inside it would put two launchers on screen fighting for the same corner.
    if (this.isEmbedded) {
      return;
    }

    const intercomConfig = new IntercomEdaraConfig(
      this.isAuthenticated,
      this.translateService.currentLang,
      this.authService.userProfile,
      this.authService.currentOrganization,
      this.authService.currentTenant,
      this.appService.state.messengerState.horizontal_padding,
      this.appService.state.messengerState.vertical_padding
    );
    this.intercom.boot(intercomConfig);
    this.translateService.onLangChange.subscribe({
      next: (translateObj: LangChangeEvent) => {
        this.intercom.update({
          alignment: translateObj.lang === 'en' ? 'right' : 'left',
          language_override: translateObj.lang === 'en' ? 'en' : 'ar-EG'
        });
      }
    });
    this.intercom.onUnreadCountChange(
      messages => (this.intercomMessages = messages)
    );
  }

  private setUserContextToSentry() {
    Sentry.setUser({
      id: this.authService.currentUserId,
      email: this.authService.currentUserEmail,
      username: this.authService.currentUserEmail,
    });

    if (this.authService.currentTenant) {
      Sentry.setExtras({
        appVersion: environment.version,
        organizationId: this.authService.currentTenant,
        organizationName: this.authService.currentOrganization?.name
      });
    }
    Sentry.setTag('Channel', 'PWA');
  }

  private async handleMultiTabs() {
    const globalSettings = await this.systemSettingsService.GlobalSettings();

    if (!globalSettings.preventMultipleTabs) return;

    // Listen for the beforeunload event to manage tab closure
    document.defaultView?.addEventListener('beforeunload', () => {
      if (this.isOldestTab) {
        this.tabLockChannel.postMessage({ type: 'OLDEST_TAB_CLOSED' });
      }
      // Send a message to the other tab to open the dialog
      this.tabLockChannel.postMessage({ type: 'CLOSE_TAB_NOTIFICATION' });
    });

    // Handle messages from the tabLockChannel
    this.tabLockChannel.onmessage = event => {
      if (event.type === 'NEW_TAB') {
        this.tabLockChannel.postMessage({ type: 'EXISTING_TAB', id: this.tabId });
      } else if (event.type === 'EXISTING_TAB') {
        if (event.id < this.tabId) {
          // Close this tab as it's newer
          document.defaultView?.close();
        } else {
          this.isOldestTab = true; // Mark this tab as the oldest
        }
      } else if (event.type === 'OLDEST_TAB_CLOSED') {
        // Check if this is now the oldest tab
        this.tabLockChannel.postMessage({ type: 'NEW_TAB' });
      } else if (event.type === 'CLOSE_TAB_NOTIFICATION') {
        // Open dialog to notify user
        this.dialog.open(MultiTabsBlockingDialogComponent, {
          width: '80vw',
          maxWidth: '600px',
          autoFocus: false,
          restoreFocus: false,
          closeOnNavigation: true,
          direction: this.translateService.instant('uiDirection')
        });
      }
    };

    // Announce this new tab
    this.tabLockChannel.postMessage({ type: 'NEW_TAB' });
  }

  private initShortcuts() {
    // Session locking belongs to the portal while embedded, so the shortcut has nothing to open.
    if (!this.isEmbedded) {
      this.shortcuts.push({
        key: ['F4', 'cmd + l'],
        allowIn: [AllowIn.Textarea, AllowIn.Input, AllowIn.Select],
        label: 'Navigation',
        description: 'Lock',
        command: () => this.lockService.show(),
        preventDefault: true
      });
    }

    this.shortcuts.push(
      {
        key: ['cmd + shift + F4'],
        allowIn: [AllowIn.Textarea, AllowIn.Input, AllowIn.Select],
        label: 'Navigation',
        description: 'Logout',
        command: () => this.authService.logout(),
        preventDefault: true
      }
    );
  }
}
